/**
 * auth.context — estado de sesión (token + usuario), persistido en localStorage.
 * Inyecta el token al http-client para todas las llamadas.
 */
import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import {
  setAuthToken,
  setRefreshHandler,
  setUnauthorizedHandler,
} from "@/shared/lib/http-client.js";
import { setFlushRefreshHandler } from "@/features/offline-sync/index.js";
import { clearOfflineData } from "@/shared/lib/idb.js";
import { clearLastSync } from "@/shared/lib/last-sync.js";
import {
  login as loginRequest,
  refresh as refreshRequest,
  type AuthUser,
} from "./services/auth.service.js";
import { createFlushRefreshHandler, shouldWipeOnLogin } from "./auth.helpers.js";

const STORAGE_KEY = "marco.auth";
// Tracks the last account whose data is cached offline on THIS device, so a
// different user logging in wipes the shared IndexedDB before loading theirs.
const LAST_USER_KEY = "marco.lastUser";

interface AuthState {
  token: string;
  refreshToken: string;
  user: AuthUser;
}

export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function loadState(): AuthState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthState) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Inicializador SINCRÓNICO: inyecta el token al http-client antes de que los
  // hijos monten y disparen sus fetch. Si se hiciera solo en useEffect, los
  // effects de los hijos corren primero (orden hijo→padre) → 401 en recarga dura.
  //
  // S4-T2: cold-start offline stale-token tolerance.
  // If the device is offline at startup, we hydrate the auth state AS-IS from
  // localStorage, even if the access token is expired. We do NOT call refresh and
  // do NOT logout. All writes go to IndexedDB (not the network), so the technician
  // can keep working. Token refresh is deferred to flush.run() on reconnect.
  //
  // ponytail: offline stale-token tolerance; ceiling = very long shift (refresh token
  // also expires); upgrade = surface login toast, keep outbox intact
  const [state, setState] = useState<AuthState | null>(() => {
    const initial = loadState();
    setAuthToken(initial?.token ?? null);
    return initial;
  });

  // Espejo del estado para leer el refreshToken vigente dentro de handlers que se
  // registran una sola vez (evita closures con valores rancios).
  const stateRef = useRef<AuthState | null>(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Mantiene el token sincronizado ante cambios posteriores (login / logout).
  useEffect(() => {
    setAuthToken(state?.token ?? null);
  }, [state]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await loginRequest(email, password);
    // Data isolation: if a DIFFERENT account previously cached data on this
    // device, wipe the offline store (and sync anchor) before loading the new
    // user's reports — otherwise stale reports leak, especially offline.
    // Same-user re-login keeps the cache so offline work survives.
    const prevUser = localStorage.getItem(LAST_USER_KEY);
    if (shouldWipeOnLogin(prevUser, res.user.id)) {
      await clearOfflineData();
      clearLastSync();
    }
    localStorage.setItem(LAST_USER_KEY, res.user.id);
    const next: AuthState = {
      token: res.token,
      refreshToken: res.refreshToken,
      user: res.user,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setState(next);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState(null);
  }, []);

  // Refresco silencioso: ante un 401, el http-client llama a este handler ANTES de
  // cerrar sesión. Lee el refreshToken vigente, pide un par nuevo y reinyecta el
  // access token. Devuelve true si renovó (→ el http-client reintenta el request),
  // false si no (→ cae al handler de 401 = logout). La llamada a /auth/refresh NO
  // pasa por el interceptor, así que no hay recursión.
  //
  // S4-T1: refreshFnRef is also read by the flush refresh handler (createFlushRefreshHandler)
  // so that flush.run() can force a token refresh before /sync.
  const refreshFnRef = useRef<(() => Promise<boolean>) | null>(null);
  useEffect(() => {
    const refreshFn = async () => {
      const current = stateRef.current;
      if (!current) return false;
      try {
        const res = await refreshRequest(current.refreshToken);
        const next: AuthState = {
          token: res.token,
          refreshToken: res.refreshToken,
          user: res.user,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        setState(next);
        setAuthToken(res.token); // inmediato: el reintento usa el token nuevo ya
        return true;
      } catch {
        return false;
      }
    };
    refreshFnRef.current = refreshFn;
    setRefreshHandler(refreshFn);
    return () => {
      refreshFnRef.current = null;
      setRefreshHandler(null);
    };
  }, []);

  // S4-T1: Register the flush refresh handler. Before each flush run(), this
  // forces a token refresh so /sync and R2 presign carry a valid Authorization.
  //
  // Uses a SEPARATE flush-specific refresh fn (flushRefreshFnRef) that is distinct
  // from the http-client refresh fn (refreshFnRef). The key difference:
  //   - refreshFnRef (http-client path): catches ALL errors and returns boolean.
  //     Required by the http-client interface; if it throws, 401 handling breaks.
  //   - flushRefreshFnRef (flush path): lets HttpError propagate so that
  //     createFlushRefreshHandler can distinguish "server said 401/403" (credential
  //     rejected → session-expired) from "network failure / 5xx" (transient → offline).
  //     Network errors still result in a no-op flush (outbox preserved, no toast).
  //
  // Bug fix: the old implementation used the http-client fn and then checked
  // `navigator.onLine` to decide whether `false` meant "session expired". That was
  // wrong because `navigator.onLine` can be true with no real connectivity. Now we
  // rely on whether the server RETURNED 401/403, not on the connectivity heuristic.
  const flushRefreshFnRef = useRef<(() => Promise<boolean>) | null>(null);
  useEffect(() => {
    const flushRefreshFn = async (): Promise<boolean> => {
      const current = stateRef.current;
      if (!current) return false;
      // Do NOT catch errors here: let HttpError(401/403) propagate so
      // createFlushRefreshHandler can classify it as "session-expired".
      // Network failures (TypeError etc.) and 5xx also propagate and are
      // treated as transient/offline by the handler (silently resolved).
      const res = await refreshRequest(current.refreshToken);
      const next: AuthState = {
        token: res.token,
        refreshToken: res.refreshToken,
        user: res.user,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setState(next);
      setAuthToken(res.token); // immediate: next request uses the new token
      return true;
    };
    flushRefreshFnRef.current = flushRefreshFn;

    const handler = createFlushRefreshHandler(() => flushRefreshFnRef.current);
    setFlushRefreshHandler(handler);
    return () => {
      flushRefreshFnRef.current = null;
      setFlushRefreshHandler(() => Promise.resolve());
    };
  }, []);

  // Sesión expirada/inválida (401 que el refresh NO pudo recuperar): avisa y cierra
  // sesión → el AuthGuard redirige a /login. Es el FALLBACK del refresco silencioso.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      toast.error("Tu sesión expiró. Iniciá sesión de nuevo.");
      logout();
    });
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{ user: state?.user ?? null, isAuthenticated: state !== null, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
